import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_BASE, formatCurrency, formatDate, formatApiErrorDetail } from "../lib/api";
import SignaturePad from "../components/app/SignaturePad";
import { CheckCircle, XCircle, ShieldCheck, CreditCard, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PublicQuote() {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [paying, setPaying] = useState(false);
  const [paymentCheckState, setPaymentCheckState] = useState(null); // null | "checking" | "paid" | "failed"
  const pollAttemptsRef = useRef(0);

  const [signerName, setSignerName] = useState("");
  const [signatureData, setSignatureData] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/public/quotes/${token}`);
      setQ(data);
    } catch (e) {
      setQ(false);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, [token]);

  async function submitSignature(e) {
    e.preventDefault();
    if (!signerName.trim()) return toast.error("Inserisci nome e cognome");
    if (!signatureData) return toast.error("Disegna la firma");
    if (!acceptedTerms) return toast.error("Accetta le condizioni");
    setSigning(true);
    try {
      const { data } = await axios.post(`${API_BASE}/public/quotes/${token}/sign`, {
        firmatario_nome: signerName,
        firma_data_url: signatureData,
      });
      setQ(data);
      toast.success("Preventivo firmato con successo");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore firma");
    } finally {
      setSigning(false);
    }
  }

  async function submitReject() {
    try {
      await axios.post(`${API_BASE}/public/quotes/${token}/reject`, { motivo: rejectReason });
      setShowRejectModal(false);
      await load();
      toast.success("Preventivo rifiutato");
    } catch (err) {
      toast.error("Errore");
    }
  }

  async function startPayment(tipo) {
    setPaying(true);
    try {
      const { data } = await axios.post(`${API_BASE}/public/quotes/${token}/checkout`, {
        tipo,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore avvio pagamento");
      setPaying(false);
    }
  }

  const pollPaymentStatus = useCallback(async (sessionId) => {
    if (pollAttemptsRef.current >= 8) {
      setPaymentCheckState("failed");
      toast.error("Verifica pagamento in corso. Ricarica la pagina tra qualche istante.");
      return;
    }
    pollAttemptsRef.current += 1;
    try {
      const { data } = await axios.get(`${API_BASE}/public/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setPaymentCheckState("paid");
        toast.success("Pagamento ricevuto. Grazie!");
        await load();
        // Rimuovi il parametro dall'URL
        const params = new URLSearchParams(searchParams);
        params.delete("payment_session");
        setSearchParams(params, { replace: true });
        return;
      }
      if (data.status === "expired") {
        setPaymentCheckState("failed");
        toast.error("Sessione di pagamento scaduta");
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId), 2000);
    } catch {
      setTimeout(() => pollPaymentStatus(sessionId), 2500);
    }
  }, [load, searchParams, setSearchParams]);

  useEffect(() => {
    const sessionId = searchParams.get("payment_session");
    const cancelled = searchParams.get("payment_cancelled");
    if (sessionId && paymentCheckState === null) {
      setPaymentCheckState("checking");
      pollPaymentStatus(sessionId);
    } else if (cancelled) {
      toast("Pagamento annullato", { description: "Puoi riprovare quando vuoi" });
      const params = new URLSearchParams(searchParams);
      params.delete("payment_cancelled");
      setSearchParams(params, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("payment_session"), searchParams.get("payment_cancelled")]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-[#64748B]">Caricamento…</div>;
  }
  if (q === false || !q) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 swiss-grid-bg">
        <div className="swiss-card p-12 text-center max-w-md">
          <XCircle size={40} weight="fill" className="mx-auto text-[#D32F2F] mb-3" />
          <h2 className="font-display text-2xl font-bold mb-2">Preventivo non trovato</h2>
          <p className="text-sm text-[#64748B]">Il link non è valido o il preventivo è stato rimosso.</p>
        </div>
      </div>
    );
  }

  const alreadySigned = q.stato === "firmato";
  const alreadyRejected = q.stato === "rifiutato";

  return (
    <div className="min-h-screen swiss-grid-bg">
      {/* Header */}
      <header className="sticky top-0 backdrop-blur-xl bg-white/80 border-b border-white/40 shadow-sm z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {q.azienda?.logo_url ? (
              <div className="w-10 h-10 bg-white border border-[#E2E8F0] rounded-sm overflow-hidden flex items-center justify-center p-1">
                <img src={q.azienda.logo_url} alt="Logo" className="w-full h-full object-contain" />
              </div>
            ) : (
              <div className="w-9 h-9 bg-[#0033A0] flex items-center justify-center rounded-sm">
                <span className="font-display font-black text-white text-xs">SG</span>
              </div>
            )}
            <div>
              <div className="font-display font-bold text-sm leading-none">{q.azienda?.ragione_sociale || "Sa.Gi Group"}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mt-1">Preventivo {q.numero}</div>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#64748B]">
            <ShieldCheck size={14} weight="bold" /> Firma Sicura
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-4 sm:p-8">
        {/* Status banners */}
        {alreadySigned && (
          <div className="swiss-card p-5 mb-6 border-l-4 border-l-[#008A3C]" data-testid="signed-banner">
            <div className="flex items-center gap-3">
              <CheckCircle size={24} weight="fill" className="text-[#008A3C]" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#008A3C] font-bold">Firmato</div>
                <div className="text-sm">Hai firmato questo preventivo il {formatDate(q.signed_at)}</div>
              </div>
            </div>
          </div>
        )}
        {alreadyRejected && (
          <div className="swiss-card p-5 mb-6 border-l-4 border-l-[#D32F2F]">
            <div className="flex items-center gap-3">
              <XCircle size={24} weight="fill" className="text-[#D32F2F]" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#D32F2F] font-bold">Rifiutato</div>
                <div className="text-sm">Hai rifiutato questo preventivo</div>
              </div>
            </div>
          </div>
        )}

        {/* Quote */}
        <div className="bg-white border border-[#E2E8F0] rounded-md shadow-xl overflow-hidden">
          <div className="p-8 sm:p-12 border-b-2 border-[#0A0A0A]">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-2">Preventivo</div>
            <div className="flex justify-between items-end flex-wrap gap-4">
              <div className="font-display text-4xl font-black tracking-tighter">{q.numero}</div>
              <div className="text-xs text-[#64748B] font-mono">
                Emesso {formatDate(q.data_emissione)}
                {q.data_validita && <> · Valido fino {formatDate(q.data_validita)}</>}
              </div>
            </div>
          </div>

          <div className="p-8 sm:p-12 grid grid-cols-1 md:grid-cols-2 gap-6 border-b border-[#E2E8F0]">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-2">Cliente</div>
              <div className="font-display text-lg font-bold">{q.cliente_snapshot?.ragione_sociale}</div>
              <div className="text-sm text-[#64748B] mt-1 space-y-0.5">
                {q.cliente_snapshot?.indirizzo && <div>{q.cliente_snapshot.indirizzo}</div>}
                {(q.cliente_snapshot?.cap || q.cliente_snapshot?.citta) && (
                  <div>{q.cliente_snapshot.cap} {q.cliente_snapshot.citta} {q.cliente_snapshot.provincia ? `(${q.cliente_snapshot.provincia})` : ""}</div>
                )}
                {q.cliente_snapshot?.p_iva && <div className="font-mono">P.IVA {q.cliente_snapshot.p_iva}</div>}
              </div>
            </div>
            <div className="md:text-right flex md:justify-end items-start gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-2">Emittente</div>
                <div className="font-display text-lg font-bold">{q.azienda?.ragione_sociale || "Sa.Gi Group"}</div>
                <div className="text-xs text-[#64748B] mt-1 space-y-0.5">
                  {q.azienda?.indirizzo && <div>{q.azienda.indirizzo}</div>}
                  {(q.azienda?.cap || q.azienda?.citta) && (
                    <div>{q.azienda.cap} {q.azienda.citta} {q.azienda.provincia ? `(${q.azienda.provincia})` : ""}</div>
                  )}
                  {q.azienda?.p_iva && <div className="font-mono">P.IVA {q.azienda.p_iva}</div>}
                  {q.azienda?.sito_web && <div>{q.azienda.sito_web}</div>}
                </div>
              </div>
              {q.azienda?.logo_url && (
                <img src={q.azienda.logo_url} alt="Logo" className="w-16 h-16 object-contain flex-shrink-0" />
              )}
            </div>
          </div>

          <div className="p-8 sm:p-12">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-4">Servizi Offerti</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#0A0A0A] text-left">
                  <th className="py-2 text-[10px] uppercase tracking-[0.15em] font-bold">Descrizione</th>
                  <th className="py-2 text-[10px] uppercase tracking-[0.15em] font-bold text-right">Q.tà</th>
                  <th className="py-2 text-[10px] uppercase tracking-[0.15em] font-bold text-right">Prezzo</th>
                  <th className="py-2 text-[10px] uppercase tracking-[0.15em] font-bold text-right">Importo</th>
                </tr>
              </thead>
              <tbody>
                {q.items.map((it, idx) => {
                  const tot = it.quantita * it.prezzo_unitario * (1 - (it.sconto_percent || 0) / 100);
                  return (
                    <tr key={idx} className="border-b border-[#E2E8F0]">
                      <td className="py-3">
                        <div className="font-semibold">{it.descrizione}</div>
                        {it.note && <div className="text-xs text-[#64748B] mt-0.5">{it.note}</div>}
                      </td>
                      <td className="py-3 text-right font-mono">{it.quantita}</td>
                      <td className="py-3 text-right font-mono">{formatCurrency(it.prezzo_unitario)}</td>
                      <td className="py-3 text-right font-mono font-bold">{formatCurrency(tot)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="flex justify-end mt-8">
              <div className="w-full sm:w-80 space-y-2">
                <Row label="Subtotale" value={formatCurrency(q.subtotale)} />
                {Number(q.rivalsa_percent) > 0 && (
                  <Row label={`+ ${q.rivalsa_label || "Rivalsa"} ${q.rivalsa_percent}%`} value={formatCurrency(q.rivalsa_importo)} />
                )}
                <Row label={`IVA ${q.iva_percent}%`} value={formatCurrency(q.iva_totale)} />
                {Number(q.ritenuta_percent) > 0 && (
                  <Row label={`− ${q.ritenuta_label || "Ritenuta"} ${q.ritenuta_percent}%`} value={`− ${formatCurrency(q.ritenuta_importo)}`} />
                )}
                <div className="bg-[#0A0A0A] text-white p-4 flex items-center justify-between mt-3">
                  <span className="text-xs uppercase tracking-[0.2em] font-bold">Totale</span>
                  <span className="font-mono text-2xl font-black">{formatCurrency(q.totale)}</span>
                </div>
              </div>
            </div>

            {(q.note || q.condizioni || q.termini_pagamento || q.metodo_pagamento) && (
              <div className="mt-8 border-t border-[#E2E8F0] pt-6 space-y-4">
                {(q.termini_pagamento || q.metodo_pagamento) && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {q.termini_pagamento && (
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-1">Termini di Pagamento</div>
                        <p className="text-sm font-semibold">{q.termini_pagamento}</p>
                      </div>
                    )}
                    {q.metodo_pagamento && (
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-1">Metodo di Pagamento</div>
                        <p className="text-sm font-semibold">{q.metodo_pagamento}</p>
                      </div>
                    )}
                  </div>
                )}
                {q.note && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-1">Note</div>
                    <p className="text-sm whitespace-pre-wrap">{q.note}</p>
                  </div>
                )}
                {q.condizioni && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-1">Condizioni</div>
                    <p className="text-sm whitespace-pre-wrap text-[#64748B]">{q.condizioni}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {alreadySigned && q.firma_data_url && (
            <div className="p-8 sm:p-12 border-t-2 border-[#008A3C] bg-[#008A3C]/5">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle size={18} weight="fill" className="text-[#008A3C]" />
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#008A3C] font-bold">Firmato da</div>
              </div>
              <div className="font-display text-lg font-bold mb-3">{q.firmatario_nome}</div>
              <div className="bg-white border border-[#E2E8F0] inline-block p-3">
                <img src={q.firma_data_url} alt="Firma" className="max-h-24" />
              </div>
            </div>
          )}
        </div>

        {/* Signature block */}
        {!alreadySigned && !alreadyRejected && (
          <div className="mt-6 bg-white border border-[#E2E8F0] rounded-md shadow-xl p-8 sm:p-12">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-2">Accettazione</div>
            <h2 className="font-display text-3xl font-black tracking-tighter mb-2">Firma il Preventivo</h2>
            <p className="text-sm text-[#64748B] mb-8">
              Per accettare il preventivo, compila i campi sottostanti e firma. Verrà inviata conferma all'emittente.
            </p>

            <form onSubmit={submitSignature} className="space-y-6" data-testid="signature-form">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">Nome e Cognome *</label>
                <input
                  required
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  data-testid="signer-name-input"
                  placeholder="Es. Mario Rossi"
                  className="w-full bg-white border border-[#E2E8F0] rounded-sm px-3 py-2.5 text-sm focus:border-[#0033A0] focus:ring-1 focus:ring-[#0033A0] outline-none"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">Firma *</label>
                <SignaturePad onChange={setSignatureData} />
              </div>
              <label className="flex items-start gap-2.5 text-sm">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  data-testid="accept-terms-checkbox"
                  className="mt-0.5 accent-[#0033A0]"
                />
                <span className="text-[#0A0A0A]">
                  Accetto i termini e le condizioni descritti nel preventivo e confermo la firma digitale per il valore totale di <strong className="font-mono">{formatCurrency(q.totale)}</strong>.
                </span>
              </label>
              <div className="flex flex-wrap gap-3 pt-2">
                <button
                  type="submit"
                  disabled={signing}
                  data-testid="confirm-sign-btn"
                  className="btn-primary px-6 py-3 text-xs uppercase tracking-[0.15em] font-bold rounded-sm flex items-center gap-2"
                >
                  <CheckCircle size={16} weight="bold" />
                  {signing ? "Invio firma…" : "Firma e Accetta"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowRejectModal(true)}
                  data-testid="reject-btn"
                  className="px-6 py-3 text-xs uppercase tracking-[0.15em] font-bold border border-[#E2E8F0] rounded-sm hover:bg-[#F4F5F7] flex items-center gap-2"
                >
                  <XCircle size={16} weight="bold" />
                  Rifiuta
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Payment block (visibile solo dopo firma e se non già pagato) */}
        {alreadySigned && q.payment_status !== "pagato" && (
          <PaymentBlock
            quote={q}
            checkState={paymentCheckState}
            paying={paying}
            onPay={startPayment}
          />
        )}

        {/* Payment confirmation banner (saldo totale) */}
        {alreadySigned && q.payment_status === "pagato" && (
          <div className="mt-6 bg-[#008A3C]/5 border-2 border-[#008A3C] rounded-md p-6 sm:p-8" data-testid="payment-confirmed-banner">
            <div className="flex items-start gap-4">
              <CheckCircle size={32} weight="fill" className="text-[#008A3C] flex-shrink-0" />
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#008A3C] font-bold mb-1">Pagato Online</div>
                <h3 className="font-display text-xl font-bold mb-2">Pagamento ricevuto · {formatCurrency(q.paid_amount || q.totale)}</h3>
                <p className="text-sm text-[#64748B]">
                  Grazie! La ricevuta è stata inviata via email da Stripe. {q.azienda?.ragione_sociale || "Sa.Gi Group"} è stata notificata.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 text-center text-[10px] uppercase tracking-[0.2em] text-[#64748B]">
          Documento generato da {q.azienda?.nome || "HACCP Consulting"} · Firma legalmente vincolante
        </div>
      </div>

      {showRejectModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowRejectModal(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#E2E8F0] rounded-md p-6 max-w-md w-full">
            <h3 className="font-display text-xl font-bold mb-3">Rifiuta Preventivo</h3>
            <p className="text-sm text-[#64748B] mb-4">Puoi indicare un motivo (opzionale).</p>
            <textarea
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] outline-none"
              placeholder="Motivo del rifiuto..."
            />
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setShowRejectModal(false)} className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold border border-[#E2E8F0] rounded-sm hover:bg-[#F4F5F7]">Annulla</button>
              <button onClick={submitReject} data-testid="confirm-reject-btn" className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold bg-[#D32F2F] text-white rounded-sm">Conferma Rifiuto</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-[#64748B]">{label}</span>
      <span className="font-mono font-bold">{value}</span>
    </div>
  );
}

function PaymentBlock({ quote, checkState, paying, onPay }) {
  const totale = Number(quote.totale || 0);
  const paid = Number(quote.paid_amount || 0);
  const residuo = Math.max(0, totale - paid);
  // Acconto suggerito (verrà ricalcolato lato server in base alle impostazioni reali)
  const accontoSuggerito = Math.round(totale * 0.5 * 100) / 100;
  const rateCount = Number(quote.abbonamento_rate_count || 12);
  const rataMensile = Math.round((totale / rateCount) * 100) / 100;
  const showSubscription = !!quote.consenti_abbonamento && rataMensile >= 1;
  const hasPartial = paid > 0;

  if (checkState === "checking") {
    return (
      <div className="mt-6 bg-white border border-[#0033A0] rounded-md shadow-xl p-8 text-center" data-testid="payment-checking">
        <Spinner size={32} className="mx-auto text-[#0033A0] animate-spin mb-3" weight="bold" />
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#0033A0] font-bold mb-1">Verifica in corso</div>
        <h3 className="font-display text-xl font-bold">Sto controllando lo stato del pagamento…</h3>
        <p className="text-sm text-[#64748B] mt-2">Non chiudere questa pagina, ci vogliono pochi secondi.</p>
      </div>
    );
  }

  return (
    <div className="mt-6 bg-white border border-[#E2E8F0] rounded-md shadow-xl p-6 sm:p-10" data-testid="payment-block">
      <div className="flex items-center gap-3 mb-2">
        <CreditCard size={24} weight="bold" className="text-[#0033A0]" />
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B]">Pagamento online</div>
      </div>
      <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tighter mb-2">
        {hasPartial ? "Salda il preventivo" : "Scegli come pagare"}
      </h2>
      <p className="text-sm text-[#64748B] mb-6">
        Pagamento sicuro con carta di credito, Apple Pay o Google Pay tramite Stripe.
        {hasPartial && <> Hai già versato <strong>{formatCurrency(paid)}</strong>.</>}
      </p>

      <div className={`grid grid-cols-1 ${showSubscription ? "md:grid-cols-3" : "sm:grid-cols-2"} gap-3 sm:gap-4`}>
        <button
          type="button"
          onClick={() => onPay("totale")}
          disabled={paying}
          data-testid="pay-totale-btn"
          className="text-left p-5 border-2 border-[#0033A0] bg-[#0033A0] text-white rounded-md hover:bg-[#00267A] disabled:opacity-50 transition-all"
        >
          <div className="text-[10px] uppercase tracking-[0.2em] opacity-70 mb-1">{hasPartial ? "Saldo residuo" : "Pagamento unico"}</div>
          <div className="font-display text-2xl font-black tracking-tighter">{formatCurrency(residuo)}</div>
          <div className="text-[11px] mt-2 opacity-80">Paghi tutto subito · ricevuta fiscale via email</div>
        </button>

        {!hasPartial && accontoSuggerito >= 1 && (
          <button
            type="button"
            onClick={() => onPay("acconto")}
            disabled={paying}
            data-testid="pay-acconto-btn"
            className="text-left p-5 border-2 border-[#0A0A0A] bg-white text-[#0A0A0A] rounded-md hover:bg-[#F4F5F7] disabled:opacity-50 transition-all"
          >
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-1">Acconto</div>
            <div className="font-display text-2xl font-black tracking-tighter">~ {formatCurrency(accontoSuggerito)}</div>
            <div className="text-[11px] mt-2 text-[#64748B]">Versa una caparra (~50%) · saldo a fine lavoro</div>
          </button>
        )}

        {!hasPartial && showSubscription && (
          <button
            type="button"
            onClick={() => onPay("abbonamento")}
            disabled={paying}
            data-testid="pay-abbonamento-btn"
            className="text-left p-5 border-2 border-[#008A3C] bg-white text-[#008A3C] rounded-md hover:bg-[#008A3C]/5 disabled:opacity-50 transition-all"
          >
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#008A3C]/80 mb-1">Rate mensili · {rateCount} mesi</div>
            <div className="font-display text-2xl font-black tracking-tighter">{formatCurrency(rataMensile)}<span className="text-sm font-normal text-[#64748B]">/mese</span></div>
            <div className="text-[11px] mt-2 text-[#64748B]">Addebito automatico mensile · cancella quando vuoi</div>
          </button>
        )}
      </div>

      {paying && (
        <div className="mt-4 text-xs text-[#64748B] flex items-center gap-2">
          <Spinner size={14} className="animate-spin" /> Apertura pagina di pagamento sicura Stripe…
        </div>
      )}

      <div className="mt-5 pt-5 border-t border-[#E2E8F0] flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#64748B]">
        <ShieldCheck size={14} weight="bold" /> Pagamento protetto · Powered by Stripe
      </div>
    </div>
  );
}
